import cn from 'classnames';
import Link from 'next/link';

/* Store */
import { useResponsiveIsMobileOrTablet } from '@store/client/store.responsive';

/* Components */

import { Grid, Badge, ELEMENT_SIZE, BADGE_TYPES } from 'ht-styleguide';
import Either from '@components/Either';
import SubscribeForm from './SubscribeForm';
import ChatLink from '@integrations/gladly/Chat/ChatLink';

/* Hooks */
import useToggleSiteNav from '@hooks/useToggleSiteNav';

/* Queries */
import useBrandingCache from '@integrations/partners/branding/useBrandingQuery.cache';

/* Constants */
import { faqPath, locationsRootPath, orderStatusPath, plansPath, productsIframeBasePath, speedTestPage } from '@constants/paths';
import { HT_INFO_SITELINKS } from '@constants/hellotech';

/* Styles */
import styles from '../footer.module.scss';
import { prevented } from '@utils/event';
import { HIDE_PRODUCT_CONNECTED_HOME } from '@constants/base';
import { PartnerClient } from '@integrations/partners/partners.types';

const Links = () => {
  const { partnerships, techs, blog, guide, careers } = HT_INFO_SITELINKS;
  const toggleSiteNav = useToggleSiteNav();
  const partner = useBrandingCache() as PartnerClient;

  return (
    <div className={styles.linksListContainer}>
      {/* HELLOTECH */}
      <div className={styles.linksCategory}>
        <h5 className="marginBottom-small1">HelloTech</h5>
        <div className={styles.linksList}>
          <Link href={plansPath()} className={styles.footerLineItem} onClick={prevented(toggleSiteNav)}>
            Services
          </Link>
          {!partner?.hideMembershipLink && (
            <Link href={plansPath()} className={styles.footerLineItem}>
              Membership
            </Link>
          )}
          {!HIDE_PRODUCT_CONNECTED_HOME && (
            <Link href={productsIframeBasePath()} className={styles.footerLineItem}>
              Products{' '}
              <Badge type={BADGE_TYPES.success} size={ELEMENT_SIZE.SMALL} variant="outlined">
                New
              </Badge>
            </Link>
          )}
          <Link href={locationsRootPath()} className={styles.footerLineItem}>
            Locations
          </Link>
        </div>
      </div>
      {/* TEAM */}
      <div className={styles.linksCategory}>
        <h5 className="marginBottom-small1">Team</h5>
        <div className={styles.linksList}>
          <a href={partnerships.url} target="_blank" rel="noreferrer" className={styles.footerLineItem}>
            Partnerships
          </a>
          <a href={careers.url} target="_blank" rel="noreferrer" className={styles.footerLineItem}>
            Careers
          </a>
          <a href={techs.url} className={styles.footerLineItem}>
            Become a Technician
          </a>
        </div>
      </div>
      {/* Support */}
      <div className={styles.linksCategory}>
        <h5 className="marginBottom-small1">Support</h5>
        <div className={styles.linksList}>
          <a href={orderStatusPath()} className={styles.footerLineItem}>
            Track my Order
          </a>
          <Link href={faqPath()} className={styles.footerLineItem}>
            FAQ
          </Link>
          <Link href={speedTestPage()} className={styles.footerLineItem}>
            Speed Test
          </Link>
          <a href={blog.url} className={styles.footerLineItem} rel="noreferrer">
            Blog
          </a>
          <a href={guide.url} className={styles.footerLineItem} rel="noreferrer">
            Guides & Tutorials
          </a>
          <ChatLink linkText="Chat with us" />
        </div>
      </div>
    </div>
  );
};

const SubscribeToNewsletter = () => (
  <div className={styles.subscriptionContainer}>
    <h5 className="marginBottom-small">Subscribe to our Newsletter</h5>
    <p className="p2 n700 marginBottom-small1">
      Join our newsletter to get the latest news and tips on home technology, plus save $10 on your next order!
    </p>
    <SubscribeForm />
  </div>
);

const SiteLinks = () => {
  const isMobileOrTablet = useResponsiveIsMobileOrTablet();

  return (
    <Grid.Fluid classes={cn(styles.borderBottom, 'paddingTop-medium2')}>
      <Either condition={isMobileOrTablet}>
        <div className={styles.siteLinksContainer}>
          <Grid.Row classes={styles.linksRow}>
            <Grid.Column sm={4} mdOffset={1} md={6}>
              <Links />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column sm={4} mdOffset={2} md={4}>
              <SubscribeToNewsletter />
            </Grid.Column>
          </Grid.Row>
        </div>

        <div className={styles.siteLinksContainer}>
          <Grid.Row>
            <Grid.Column lgOffset={1} lg={6}>
              <Links />
            </Grid.Column>
            <Grid.Column lg={4}>
              <SubscribeToNewsletter />
            </Grid.Column>
          </Grid.Row>
        </div>
      </Either>
    </Grid.Fluid>
  );
};

export default SiteLinks;

/*
  SiteLinks: Section of the footer that contains links to various parts around the site; the top portion of the complete header.
*/
